import type { CalendarEvent } from '../types.js';
import type { CalendarMessages } from '../i18n.js';

export type CalendarLocationType = 'online' | 'inPerson' | 'custom';
export type CalendarMeetingProvider =
  | 'tencent'
  | 'zoom'
  | 'googleMeet'
  | 'teams'
  | 'other';

export interface CalendarLocationState {
  readonly locationType: CalendarLocationType | null;
  readonly meetingProvider: CalendarMeetingProvider | null;
  readonly meetingDetail: string | null;
  readonly locationText: string | null;
}

export const MEETING_PROVIDER_VALUES: readonly CalendarMeetingProvider[] = [
  'tencent',
  'zoom',
  'googleMeet',
  'teams',
  'other',
];

export function defaultCalendarLocationState(): CalendarLocationState {
  return {
    locationType: null,
    meetingProvider: 'tencent',
    meetingDetail: null,
    locationText: null,
  };
}

export function normalizeCalendarLocationState(
  input: Partial<CalendarLocationState>,
): CalendarLocationState {
  const locationType = input.locationType ?? null;
  if (locationType === null) return defaultCalendarLocationState();
  if (locationType === 'online') {
    return {
      locationType,
      meetingProvider: input.meetingProvider ?? 'tencent',
      meetingDetail: clean(input.meetingDetail),
      locationText: null,
    };
  }
  return {
    locationType,
    meetingProvider: null,
    meetingDetail: null,
    locationText: clean(input.locationText),
  };
}

export function providerLabel(
  provider: CalendarMeetingProvider | null | undefined,
  messages: CalendarMessages,
): string {
  switch (provider ?? 'tencent') {
    case 'tencent':
      return messages.meetingProviderTencent;
    case 'zoom':
      return messages.meetingProviderZoom;
    case 'googleMeet':
      return messages.meetingProviderGoogleMeet;
    case 'teams':
      return messages.meetingProviderTeams;
    case 'other':
      return messages.meetingProviderOther;
  }
}

export function formatEventLocation(
  event: Pick<
    CalendarEvent,
    'locationType' | 'meetingProvider' | 'meetingDetail' | 'locationText'
  >,
  messages: CalendarMessages,
): string | null {
  const state = normalizeCalendarLocationState(event);
  if (state.locationType === null) return null;
  if (state.locationType === 'online') {
    const label = providerLabel(state.meetingProvider, messages);
    return state.meetingDetail === null ? label : `${label}: ${state.meetingDetail}`;
  }
  return state.locationText;
}

export function isLikelyMeetingUrl(value: string | null | undefined): value is string {
  if (value === null || value === undefined) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}
